import { useEffect, useRef } from 'react'
import { usePOSStore } from '@/store/posStore'
import { liveSyncService, SyncMessage } from '@/services/liveSyncService'

/**
 * useLiveSync - Hook that enables real-time data synchronization across browser tabs.
 *
 * Place this once at the app level (e.g., inside a layout or App component).
 * It handles:
 * 1. Cross-tab sync: When another tab makes a data change, this tab refreshes.
 * 2. Visibility refresh: When this tab becomes visible after being idle, stale data is refreshed.
 *
 * Note: products, customers, and batches are handled by Firestore real-time listeners
 * (initRealtimeListeners in posStore). This hook only manages REST-fetched collections:
 * categories, suppliers, grns, expenses.
 */
export function useLiveSync() {
  const {
    fetchCategories,
    fetchSuppliers,
    fetchGRNs,
    fetchExpenses,
  } = usePOSStore()

  // products, customers, and batches are handled by Firestore real-time listeners
  const fetchMapRef = useRef<Record<string, (() => Promise<void>) | undefined>>({
    categories: fetchCategories,
    suppliers: fetchSuppliers,
    grns: fetchGRNs,
    expenses: fetchExpenses,
    transactions: undefined, // Transactions don't have a fetch-all in the store
  })

  // Keep the ref updated with the latest fetch functions
  useEffect(() => {
    fetchMapRef.current = {
      categories: fetchCategories,
      suppliers: fetchSuppliers,
      grns: fetchGRNs,
      expenses: fetchExpenses,
      transactions: undefined,
    }
  }, [fetchCategories, fetchSuppliers, fetchGRNs, fetchExpenses])

  useEffect(() => {
    // Handler for cross-tab sync messages
    const handleSyncMessage = (message: SyncMessage) => {
      const fetcher = fetchMapRef.current[message.collection]
      if (fetcher) {
        console.log(`[LiveSync] Refreshing ${message.collection} due to ${message.action} from another tab`)
        fetcher()
      }
      // products/customers/batches sync is handled by Firestore listeners — no manual refresh needed
    }

    // Handler for visibility-based refresh (tab becomes visible after being idle)
    const handleVisibilityRefresh = () => {
      const staleCollections = liveSyncService.getStaleCollections()
      staleCollections.forEach(collection => {
        const fetcher = fetchMapRef.current[collection]
        if (fetcher) {
          console.log(`[LiveSync] Visibility refresh: ${collection}`)
          fetcher()
        }
      })
    }

    // Initialize the service with the visibility handler
    liveSyncService.init(handleVisibilityRefresh)

    // Subscribe to all sync events
    const unsubscribe = liveSyncService.onAny(handleSyncMessage)

    return () => {
      unsubscribe()
      liveSyncService.destroy()
    }
  }, []) // Empty deps - runs once on mount, cleans up on unmount

  return {
    tabId: liveSyncService.getTabId(),
  }
}
